import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import EMAIL_CONFIG from "@/config/emailConfig";
import usePageTitle from "@/hooks/usePageTitle";

async function hashPassword(password) {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function AdminLogin() {
  usePageTitle("Admin Login", "ElitePlus admin panel login.");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const hash = await hashPassword(password);
      if (hash === EMAIL_CONFIG.adminPasswordHash) {
        sessionStorage.setItem("admin_auth", hash);
        navigate("/admin");
      } else {
        setError("Invalid password");
      }
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={`${process.env.PUBLIC_URL}/eliteplus_logo.png`} alt="Logo" className="w-12 h-12 object-contain" />
            <span className="text-3xl font-bold text-slate-800">
              Elite<span className="text-cyan-500">Plus</span>
            </span>
          </div>
          <h1 className="text-xl font-semibold text-slate-700" data-testid="admin-login-title">Admin Panel</h1>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label className="text-slate-700">Admin Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  data-testid="admin-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 bg-slate-50 border-slate-200"
                  placeholder="Enter admin password"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold"
            >
              {loading ? "Verifying..." : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
